import { FC, useEffect, useState } from "react";
import PageTitle from "../../assets/components/PageTitle/PageTitle";
import TransactionsSearchBar from "./TransactionsTable/TransactionsSearchBar";
import { Products } from "../../models/models";
import { SearchBarDataHandler } from "../../assets/components/SearchBar/SearchBar";
import { toast } from "react-toastify";
import {
    useGetProductsListQuery,
    useGetTransactionsListQuery,
    useRetryTransactionsMutation,
    useRetryAllFailedTransactionsMutation,
} from "../../redux/RTKQuery/api";
import { Loading, uiSlicedActions } from "../../redux/ui-slice";
import { useAppDispatch, useAppSelector } from "../../assets/hooks/hooks";
import { Page } from "../../assets/components/PaginationBar/PaginationBar";
import Loader from "../../assets/components/Loader/Loader";
import { Transaction, TransactionStatus } from "./model";
import TransactionsTableHeader from "./TransactionsTable/TransactionsTableHeader";
import TransactionsTable from "./TransactionsTable/TransactionsTable";

const Transactions: FC = () => {
    const dispatch = useAppDispatch();
    const [products, setDataProducts] = useState<Products>([]);
    const [quiresData, setQuires] = useState<SearchBarDataHandler>({});
    const loading: Loading = useAppSelector((state) => state.ui.loading);
    const pageSize: number = 30;
    const [dataTable, setData] = useState<Transaction[]>([]);
    const [pageNumber, setCurrentPage] = useState<Page>(1);
    const [totalTransactions, setTotalTransactions] = useState<number>(0);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);

    const failedInPage = dataTable.filter((t) => t.status === TransactionStatus.Failed);
    const isAllSelected =
        failedInPage.length > 0 &&
        failedInPage.every((t) => selectedIds.includes(t.id));

    // Selection handlers - only select failed transactions
    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            const failedIds = dataTable
                .filter((t) => t.status === TransactionStatus.Failed)
                .map((t) => t.id);
            setSelectedIds(failedIds);
        } else {
            setSelectedIds([]);
        }
    };

    const handleSelectRow = (id: number, checked: boolean) => {
        if (checked) {
            setSelectedIds((prev) => [...prev, id]);
        } else {
            setSelectedIds((prev) => prev.filter((selectedId) => selectedId !== id));
        }
    };

    // Reset selection when page changes
    useEffect(() => {
        setSelectedIds([]);
    }, [pageNumber]);

    const {
        data: dataProducts,
        isSuccess: isSuccessProducts,
        isError: isErrorProducts,
        isLoading: isLoadingProducts,
    } = useGetProductsListQuery(
        { pageNumber: 1, pageSize: 30 },
        { refetchOnFocus: true },
    );

    const {
        data: dataTransactions,
        isLoading: isLoadingTransactions,
        isSuccess: isSuccessTransactions,
        isError: isErrorTransactions,
    } = useGetTransactionsListQuery(
        {
            pageNumber,
            pageSize,
            orderId: quiresData.searchByOrderId,
            transactionId: quiresData.searchInput,
            productId: quiresData.productId,
            startDate: quiresData.startDate,
            endDate: quiresData.endDate,
        },
        { refetchOnFocus: true },
    );

    // Query to get total count of failed transactions (across all pages)
    const { data: failedTransactionsData } = useGetTransactionsListQuery(
        {
            pageNumber: 1,
            pageSize: 1,
            orderId: quiresData.searchByOrderId,
            transactionId: quiresData.searchInput,
            productId: quiresData.productId,
            startDate: quiresData.startDate,
            endDate: quiresData.endDate,
            status: TransactionStatus.Failed,
        },
        { refetchOnFocus: true },
    );

    const [
        retryTransactions,
        {
            isLoading: isLoadingRetry,
            isSuccess: isSuccessRetry,
            isError: isErrorRetry,
            data: retryData,
            reset: resetRetry,
        },
    ] = useRetryTransactionsMutation();

    const [
        retryAllFailed,
        {
            isLoading: isLoadingRetryAll,
            isSuccess: isSuccessRetryAll,
            isError: isErrorRetryAll,
            data: retryAllData,
            reset: resetRetryAll,
        },
    ] = useRetryAllFailedTransactionsMutation();

    const isRetrying = isLoadingRetry || isLoadingRetryAll;

    const failedCount =
        failedTransactionsData?.filteredCount ??
        failedTransactionsData?.totalCount ??
        0;

    // Retry handlers
    const handleRetrySelected = async () => {
        await retryTransactions({ ids: selectedIds });
    };

    const handleRetryAllFailed = async () => {
        await retryAllFailed({
            orderId: quiresData.searchByOrderId,
            transactionId: quiresData.searchInput,
            productId: quiresData.productId,
            startDate: quiresData.startDate,
            endDate: quiresData.endDate,
            status: TransactionStatus.Failed,
        });
    };

    // Handle loading state
    useEffect(() => {
        const loadingGlobal =
            isLoadingProducts || isLoadingTransactions || isRetrying;
        dispatch(
            uiSlicedActions.setLoading(loadingGlobal ? "pending" : "hidden"),
        );
    }, [isLoadingProducts, isLoadingTransactions, isRetrying, dispatch]);

    // Handle transactions data
    useEffect(() => {
        if (isSuccessTransactions && dataTransactions) {
            setData(dataTransactions.items);
            if (dataTransactions.filteredCount !== undefined) {
                setTotalTransactions(dataTransactions.filteredCount);
            } else {
                setTotalTransactions(dataTransactions.totalCount);
            }
        }
        if (isErrorTransactions) {
            toast.error("Error loading transactions");
        }
    }, [
        dataTransactions,
        isErrorTransactions,
        isSuccessTransactions,
        pageNumber,
        pageSize,
    ]);

    // Handle products data
    useEffect(() => {
        if (isSuccessProducts && dataProducts) {
            setDataProducts(dataProducts.products);
        }
        if (isErrorProducts) {
            toast.error("Error loading products");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSuccessProducts, isErrorProducts]);

    // Handle retry success/error
    useEffect(() => {
        if (isSuccessRetry && retryData) {
            toast.success(
                `Retry started for ${retryData.retried} transaction${retryData.retried > 1 ? "s" : ""}`
            );
            setSelectedIds([]);
            resetRetry();
        }
        if (isErrorRetry) {
            toast.error("Failed to retry transactions");
            resetRetry();
        }
    }, [isSuccessRetry, isErrorRetry, retryData, resetRetry]);

    useEffect(() => {
        if (isSuccessRetryAll && retryAllData) {
            toast.success(
                `Retry started for ${retryAllData.retried} failed transaction${retryAllData.retried > 1 ? "s" : ""}`
            );
            resetRetryAll();
        }
        if (isErrorRetryAll) {
            toast.error("Failed to retry transactions");
            resetRetryAll();
        }
    }, [isSuccessRetryAll, isErrorRetryAll, retryAllData, resetRetryAll]);

    return (
        <div className="page_with_table">
            <Loader loading={loading} />

            <PageTitle text="Transactions" />
            <TransactionsSearchBar
                dataHandler={setQuires}
                products={products}
                dateRange={{
                    startDate: quiresData.startDate,
                    endDate: quiresData.endDate,
                }}
            />

            <div className="table_scroll_wrapper">
                <TransactionsTableHeader
                    pageNumber={pageNumber}
                    sizePage={pageSize}
                    dataTable={dataTable}
                    totalTransactions={totalTransactions}
                    setCurrentPage={setCurrentPage}
                    currentSizePage={pageSize}
                    selectedCount={selectedIds.length}
                    failedCount={failedCount}
                    onRetrySelected={handleRetrySelected}
                    onRetryAllFailed={handleRetryAllFailed}
                    isLoading={isRetrying}
                    isAllSelected={isAllSelected}
                    onSelectAll={handleSelectAll}
                />

                <TransactionsTable
                    transactions={dataTable}
                    products={products}
                    selectedIds={selectedIds}
                    onSelectAll={handleSelectAll}
                    onSelectRow={handleSelectRow}
                />
            </div>
        </div>
    );
};

export default Transactions;
